import * as THREE from "three";
import { MathUtils, Vector3 } from "three";
import { generateUUID } from "three/src/math/MathUtils";
import { IndserObject, IndsertPosition } from "@/core/IndserObject";
import { MLayer } from "@/core/MLayer";
import {
  DBEntity,
  DbLayer,
  DbLine,
  FMathUtils,
  ModelManager,
  ViewManager,
} from "@/CadEngine";
import { BPMathUtils } from "@/utils/BPMathUtils";
import {
  DrawOperateState,
  IDrawOperation,
  DrawOperationMovePointBeforeParams,
  DrawOperationMovePointParams,
} from "../IDrawOperation";
import { BPLinear } from "@/core/BPLinear";
import { Context } from "@/core/context";
import { ULabel } from "@/ui/ULabel";
import { ProtectRadius } from "./ProtectRadius";
import { ActiveInputType, CommonColor, FinishResult } from "@/core/Parameters";
import { DimensionGroup } from "@/core/DimensionGroup";

class DrawRectangleLayout {
  private m_model: ModelManager;
  private m_layer: DbLayer;
  private m_auxLayer: MLayer;
  private m_material: THREE.LineBasicMaterial;
  private m_tempObject: THREE.Line | undefined;
  private m_geometry: THREE.BufferGeometry | undefined;
  private points: THREE.Vector3[] = [];
  private m_locationObject: THREE.Line;
  private m_size = { x: 1, y: 1, z: 1 };
  private view: ViewManager;
  private m_auxPoints: {
    orgin: THREE.Vector3;
    dir: THREE.Vector3 | undefined;
  }[] = [];
  private m_preMovePoint: { point: THREE.Vector3; time: number } = {
    point: new THREE.Vector3(),
    time: 0,
  };
  private m_curPoint: THREE.Vector3 = new THREE.Vector3();
  private m_dimension: DimensionGroup;
  private m_context: Context;
  private AuxPointGroup: THREE.Group;
  private m_specialParams: any;
  private ProtectRadiusGroup: THREE.Group;
  private XAxisAuxPointGroup: THREE.Group;
  private XAxisProtectRadiusGroup: THREE.Group;
  private m_activeInputType: ActiveInputType = ActiveInputType.None;
  private m_inputValue: string = "";
  private m_distanceInputValue: string = "";
  private m_angleInputValue: string = "";
  private m_quadraturePoint: THREE.Vector3 | undefined = undefined;
  private m_isEnter: boolean = false;
  private m_color: number;

  constructor(
    context: Context,
    layer: DbLayer,
    color: number,
    specialParams?: any
  ) {
    this.m_context = context;
    this.m_model = context.ModelManager;
    this.m_layer = layer;
    this.m_color = color;
    this.m_auxLayer = context.SceneManager.AuxModel;
    this.m_material = new THREE.LineBasicMaterial({
      color: new THREE.Color(color),
    });
    this.m_locationObject = context.ToolsManager.CreatedLocationLine();
    this.view = context.ViewManager;
    this.m_dimension = new DimensionGroup(this.view, this.m_auxLayer);
    this.m_dimension.Line.SetVaule("");
    this.m_dimension.Arc.SetVaule("");
    this.AuxPointGroup = new THREE.Group();
    this.m_auxLayer.add(this.AuxPointGroup);
    this.m_specialParams = specialParams;
    let XLabel = this.createDistanceLabel();
    this.XAxisLabelArray.push(XLabel);
    let YLabel = this.createDistanceLabel();
    this.YAxisLabelArray.push(YLabel);
    this.createRectangle();
    this.ProtectRadiusGroup = new THREE.Group();
    this.m_auxLayer.add(this.ProtectRadiusGroup);
    this.XAxisAuxPointGroup = new THREE.Group();
    this.m_auxLayer.add(this.XAxisAuxPointGroup);
    this.XAxisProtectRadiusGroup = new THREE.Group();
    this.m_auxLayer.add(this.XAxisProtectRadiusGroup);
    this.m_context.Dom.style.cursor = "none";
  }

  onMouseMove(e: PointerEvent): void {}

  private isLineInput: boolean = true;
  private angleNum: number | undefined;
  private lineLengthInputValue: string = "";
  private isAllowInput: boolean = false;
  onKeyDown(e: KeyboardEvent): void {
    e.preventDefault();
    if (this.points.length == 0) return;
    if (this.points.length >= 2) return;
    this.lineLengthInputValue = this.m_dimension.Line.Input.Dom.value;
    if (e.key === "Tab") {
      this.m_inputValue = "";
      if (this.m_activeInputType == ActiveInputType.None) {
        this.m_activeInputType = ActiveInputType.Distance;
        this.m_dimension.Line.Input.Dom.style.border = "1px solid #ffffff";
        this.m_dimension.Arc.Input.Dom.style.border = "1px solid #101010";
      } else if (this.m_activeInputType == ActiveInputType.Distance) {
        this.m_activeInputType = ActiveInputType.Angle;
        this.m_dimension.Line.Input.Dom.style.border = "1px solid #101010";
        this.m_dimension.Arc.Input.Dom.style.border = "1px solid #ffffff";
      } else if (this.m_activeInputType == ActiveInputType.Angle) {
        this.m_activeInputType = ActiveInputType.Distance;
        this.m_dimension.Line.Input.Dom.style.border = "1px solid #ffffff";
        this.m_dimension.Arc.Input.Dom.style.border = "1px solid #101010";
      }
    }
    if (e.key == "Backspace") {
      if (this.m_inputValue.length > 0) {
        this.m_inputValue = this.m_inputValue.substring(
          0,
          this.m_inputValue.length - 1
        );
      }
      if (this.m_activeInputType == ActiveInputType.Distance) {
        this.m_dimension.Line.Input.SetVaule(this.m_inputValue);
        if (this.m_inputValue) {
          this.m_distanceInputValue = this.m_inputValue;
        }
      } else if (this.m_activeInputType == ActiveInputType.Angle) {
        this.m_dimension.Arc.Input.SetVaule(this.m_inputValue + " °");
        if (this.m_inputValue) {
          this.m_angleInputValue = this.m_inputValue;
        }
      }
    } else if ((e.key >= "0" && e.key <= "9") || e.key == ".") {
      if (this.m_activeInputType == ActiveInputType.None) return;
      if (this.m_inputValue?.includes(".") && e.key == ".") return;
      this.m_inputValue += e.key;
      if (this.m_activeInputType == ActiveInputType.Distance) {
        this.m_dimension.Line.Input.SetVaule(this.m_inputValue);
        this.m_distanceInputValue = this.m_inputValue;
      } else if (this.m_activeInputType == ActiveInputType.Angle) {
        this.m_dimension.Arc.Input.SetVaule(this.m_inputValue + " °");
        this.m_angleInputValue = this.m_inputValue;
      }
    } else if (e.key == "Enter") {
      let length = parseFloat(this.m_distanceInputValue) * 0.001;
      let position = this.m_locationObject.position.clone();
      let curDir = position
        .clone()
        .sub(this.points[this.points.length - 1])
        .normalize();
      let angle = curDir.angleTo(new THREE.Vector3(1, 0, 0));
      if (curDir.clone().cross(new THREE.Vector3(1, 0, 0)).z > 0) {
        angle = -angle;
      }
      curDir.applyAxisAngle(new THREE.Vector3(0, 0, 1), -angle);
      curDir.applyAxisAngle(
        new THREE.Vector3(0, 0, 1),
        MathUtils.degToRad(parseFloat(this.m_angleInputValue))
      );
      position = this.points[this.points.length - 1]
        .clone()
        .add(curDir.multiplyScalar(length));
      this.m_isEnter = true;
      this.m_quadraturePoint = undefined;
      this.OnMovePoint({
        point: position,
        geo: undefined,
        position: IndsertPosition.None,
      });
      this.m_isEnter = false;
      this.points.push(position);
      this.view.Render();
      this.angleNum = undefined;
      this.lineLengthInputValue = "";
    }
  }
  onKeyUp(e: KeyboardEvent): void {}

  private get originPoint() {
    return this.points[0];
  }
  private get curPoint() {
    return this.m_curPoint;
  }

  private get curDir() {
    return this.curPoint.clone().sub(this.originPoint).normalize();
  }
  private drawLineAux: THREE.Line | undefined;
  private SignMatrixs: THREE.Matrix4[] = [];

  OnZoom(camera: THREE.Camera) {
    this.m_auxPoints = [];
  }
  OnMovePointBefore(params: DrawOperationMovePointBeforeParams): void {
    let tolSq = params.tol * params.tol;
    if (this.points.length > 0 && this.points.length <= 2) {
      let start = new THREE.Vector3();
      let end = new THREE.Vector3();
      let target = new THREE.Vector3();
      let targetOnSegment = new THREE.Vector3();
      let point = this.points[this.points.length - 1];
      if (this.points.length > 1) {
        for (let index = 0; index < this.points.length - 1; index++) {
          start.copy(this.points[index]);
          end.copy(this.points[index + 1]);
          let distance = params.ray.distanceSqToSegment(
            start,
            end,
            target,
            targetOnSegment
          ); // target在射线上，targetOnSegment在线段上
          //  console.log(target.x,target.y,target.z+'          '+targetOnSegment.x,targetOnSegment.y,targetOnSegment.z)
          if (distance <= tolSq) {
            // let location = {x:Number(target.x.toFixed(3)), y:Number(target.y.toFixed(3)), z:Number(target.z.toFixed(3))};
            let location = {
              x: Number(targetOnSegment.x.toFixed(3)),
              y: Number(targetOnSegment.y.toFixed(3)),
              z: Number(targetOnSegment.z.toFixed(3)),
            };
            let center = new THREE.Vector3(
              (start.x + end.x) * 0.5,
              (start.y + end.y) * 0.5,
              (start.z + end.z) * 0.5
            );
            let d1 = start.distanceTo(targetOnSegment);
            let d2 = center.distanceTo(targetOnSegment);
            let d3 = end.distanceTo(targetOnSegment);
            let position = IndsertPosition.None;
            let min = Math.min(d1, d2, d3);
            let adjust = targetOnSegment.clone();
            if (min <= params.tol) {
              if (min == d1) {
                position = IndsertPosition.Start;
                adjust = start.clone();
              }
              if (min == d2) {
                position = IndsertPosition.Center;
                adjust = center;
              }
              if (min == d3) {
                position = IndsertPosition.End;
                adjust = end.clone();
              }
            }
            let insert = new IndserObject(location, distance, "", []);
            insert.adjust = adjust;
            insert.position = position;
            insert.geo = new THREE.Line3(start.clone(), end.clone());
            params.array.push(insert);
          }
        } // 以上先是用当前移动线检测是否和自己画的线段相交，不包括最后一个线段

        //夹角矫正
        {
          // 这段代码作用为用当前移动线段来和自己画的线的最后一段线检测是否相交
          // alert('yyyyyyyy')
          let direction = new THREE.Vector3();
          this.view.Camera.getWorldDirection(direction);
          let line = new THREE.Line3(
            this.points[this.points.length - 2].clone(),
            this.points[this.points.length - 1].clone()
          );
          let p0 = new THREE.Vector3();
          let p = params.ray.intersectPlane(new THREE.Plane(direction, 0), p0);
          let dir0 = line.end.clone().sub(line.start).normalize();
          let dir1 = p0.clone().sub(line.end).normalize();
          let rad = BPMathUtils.angleTo(dir0, dir1, direction);
          let angle = (rad * 180) / Math.PI;
          let orginLength = p0.distanceTo(line.end);
          let tolAngle = (((params.tol / orginLength) * 180) / Math.PI) * 2; // orginLength越小自动偏转幅度越大

          let offsetAngle = (angle + tolAngle) % 90;
          if (offsetAngle <= tolAngle * 2) {
            //五度之内视为垂直
            rad = rad - ((offsetAngle - tolAngle) * Math.PI) / 180;
            let p = dir0
              .applyAxisAngle(direction, rad)
              .multiplyScalar(p0.distanceTo(line.end));

            p = line.end.clone().add(dir0);
            let insert = new IndserObject(
              p,
              Math.sqrt(params.ray.distanceSqToPoint(p)),
              "",
              []
            );
            insert.adjust = p;
            insert.position = IndsertPosition.AuxOrthogonal;
            params.array.push(insert);
          }
        }
      }
      //正交矫正
      {
        // 这段代码作用为 当只有一个点的时候移动线段和 X轴的相交情况
        let direction = new THREE.Vector3();
        this.view.Camera.getWorldDirection(direction);
        let start = this.points[this.points.length - 1].clone();
        let p0 = new THREE.Vector3();
        params.ray.intersectPlane(new THREE.Plane(direction, 0), p0);
        let dir0 = new THREE.Vector3(1, 0, 0);
        let dir1 = p0.clone().sub(start).normalize();
        let rad = BPMathUtils.angleTo(dir0, dir1, direction);
        let angle = (rad * 180) / Math.PI;
        let orginLength = p0.distanceTo(start);
        let tolAngle = (((params.tol / orginLength) * 180) / Math.PI) * 2;
        let offsetAngle = (angle + tolAngle) % 90;

        if (offsetAngle <= tolAngle * 2) {
          //五度之内视为垂直
          rad = rad - ((offsetAngle - tolAngle) * Math.PI) / 180;
          let p = dir0
            .applyAxisAngle(direction, rad)
            .multiplyScalar(p0.distanceTo(start));
          p = start.clone().add(dir0);
          let insert = new IndserObject(
            p,
            Math.sqrt(params.ray.distanceSqToPoint(p)),
            "",
            []
          );
          insert.adjust = p;
          insert.position = IndsertPosition.AuxOrthogonal;
          // insert.geo = new THREE.Line3(start.clone(), end.clone());
          params.array.push(insert);
        }
      }
    }
    let ray = new THREE.Ray();

    /**
     * 辅助点的吸附点
     */
    if (this.m_auxPoints.length > 0) {
      let normal = new THREE.Vector3(0, 0, 1);
      let panel = new THREE.Plane(normal, 0);
      let insertPoint = new THREE.Vector3();
      params.ray.intersectPlane(panel, insertPoint);

      let func = (line: BPLinear, position: IndsertPosition) => {
        let target = new THREE.Vector3();
        let dis = line.distanceSqPoint(insertPoint, target); //line.distanceSqToRay(params.ray, target);
        if (dis <= tolSq) {
          let insert = new IndserObject(target, Math.sqrt(dis), "", []);
          insert.adjust = target;
          insert.position = position;
          insert.geo = line;
          params.array.push(insert);
        }
      };

      this.m_auxPoints.forEach((item) => {
        if (item.orgin && item.dir) {
          if (
            item.dir.distanceTo(new THREE.Vector3(1, 0, 0)) <= 0.0000001 ||
            item.dir.distanceTo(new THREE.Vector3(-1, 0, 0)) <= 0.0000001 ||
            item.dir.distanceTo(new THREE.Vector3(0, 1, 0)) <= 0.0000001 ||
            item.dir.distanceTo(new THREE.Vector3(0, -1, 0)) <= 0.0000001
          ) {
            func(
              new BPLinear(item.orgin, item.dir),
              IndsertPosition.AuxVertical
            );
            func(
              new BPLinear(
                item.orgin,
                item.dir.clone().applyAxisAngle(normal, Math.PI * 0.5)
              ),
              IndsertPosition.AuxVertical
            );
          } else {
            func(
              new BPLinear(item.orgin, item.dir),
              IndsertPosition.AuxVertical
            );
            func(
              new BPLinear(
                item.orgin,
                item.dir.clone().applyAxisAngle(normal, Math.PI * 0.5)
              ),
              IndsertPosition.AuxVertical
            );
            func(
              new BPLinear(item.orgin, new THREE.Vector3(1, 0, 0)),
              IndsertPosition.AuxOrthogonal
            );
            func(
              new BPLinear(item.orgin, new THREE.Vector3(0, 1, 0)),
              IndsertPosition.AuxOrthogonal
            );
          }
        }
      });
    }

    params.array.forEach((item) => {
      if (item.position == "none") item.position = IndsertPosition.Insert;
      if (item.geo) {
        if (item.geo instanceof THREE.Line3) {
          let line = item.geo as THREE.Line3;
          let p0 = item.adjust ?? item.location;
          let v0 = line.start.clone().sub(line.end.clone());
          let dir0 = v0.clone().normalize();
          if (this.points.length > 0) {
            let point = this.points[this.points.length - 1];
            let v1 = new THREE.Vector3(
              p0.x - point.x,
              p0.y - point.y,
              p0.z - point.z
            );
            let offset = dir0.multiplyScalar(v1.dot(dir0));
            if (offset.lengthSq() <= params.tol * params.tol) {
              //正交矫正
              item.position = IndsertPosition.Vertical;
              item.adjust = new THREE.Vector3(
                p0.x - offset.x,
                p0.y - offset.y,
                p0.z - offset.z
              );
            }
          }
          ray.set(line.start, line.end.clone().sub(line.start).normalize()); // 这里开始检测移动线段是否和场景中两个或两个以上的交点处接触
          params.array.forEach((item1) => {
            if (item1.geo instanceof THREE.Line3) {
              let line1 = item1.geo as THREE.Line3;
              // console.log(params,line,line1,line.start.clone().distanceTo(line1.start.clone()),'!!!!!!!!!');
              if (
                line != line1 &&
                line.start.clone().distanceTo(line1.start.clone()) >
                  0.0000000001 &&
                line.end.clone().distanceTo(line1.end.clone()) >
                  0.00000000001 &&
                line.start.y != line1.end.y
              ) {
                let target = new THREE.Vector3();
                let target1 = new THREE.Vector3();
                let distance = ray.distanceSqToSegment(
                  line1.start.clone(),
                  line1.end.clone(),
                  target
                );
                if (distance < 0.00000000001) {
                  line.closestPointToPoint(target, true, target1); //
                  if (target.distanceTo(target1) < 0.00000001) {
                    // 说明这两条线交叉了，因为这个点同时在两根线上
                    item.position = IndsertPosition.Cross;
                    item.adjust = target;
                    item.distance = target.distanceTo(
                      new THREE.Vector3(
                        item.location.x,
                        item.location.y,
                        item.location.z
                      )
                    );
                  }
                }
              }
            }
          });
        }
      }
    });
  }
  OnMovePoint(params: DrawOperationMovePointParams): void {
    this.m_locationObject.position.copy(params.point);
    this.m_locationObject.visible = true;
    if (this.points.length >= 2) {
      this.OnWheel();
      return;
    }
    this.curPoint.copy(params.point);
    if (this.points.length == 0) return;
    let ps = [];
    ps.push(...this.points);
    let pss = [];
    pss = [...ps, params.point];

    let paramsClone = params.point.clone();
    paramsClone.sub(this.points[this.points.length - 1].clone()).normalize();
    if (this.drawLineAux) {
      let arr = [ps[ps.length - 1], params.point];
      if (this.m_context.DrawManager.isEnableQuadrature && !this.m_isEnter) {
        this.m_quadraturePoint =
          this.m_context.ToolsManager.GetOrthogonalityPoint(
            params.point,
            ps[ps.length - 1]
          );
        arr = [ps[ps.length - 1], this.m_quadraturePoint];
      }
      this.drawLineAux.geometry.setFromPoints(arr);
      this.drawLineAux.computeLineDistances();
    }

    if (this.m_geometry) {
      this.m_geometry.setFromPoints(ps);
      this.m_tempObject!.computeLineDistances();
    } else {
      this.m_geometry = new THREE.BufferGeometry();
      this.m_geometry.setFromPoints(ps);
      this.m_tempObject = new THREE.Line(this.m_geometry, this.m_material);
      this.m_tempObject.computeLineDistances();
      this.m_tempObject.frustumCulled = false;
      this.m_auxLayer.add(this.m_tempObject);
    }
    let xAxis = new THREE.Vector3(1, 0, 0);
    this.m_dimension.Line.SetDimension(
      this.originPoint,
      this.curPoint,
      this.scale
    );
    this.m_dimension.Arc.SetDimension(
      this.originPoint,
      xAxis,
      paramsClone.clone().normalize(),
      this.scale
    );
    this.m_dimension.Anchor.SetPoint(this.auxPoint, this.scale);
    let angle = this.curDir.clone().angleTo(xAxis);
    if (this.curDir.clone().cross(xAxis).z > 0) {
      angle = Math.PI * 2 - angle;
    }
    angle = (angle / Math.PI) * 180;
    let distance = (this.curPoint.distanceTo(this.originPoint) * 1000)
      .toFixed(2)
      .toString();
    this.m_dimension.Line.SetVaule(distance);
    this.m_distanceInputValue = distance;
    this.m_dimension.Arc.SetVaule(angle.toFixed(0).toString());
    this.m_angleInputValue = angle.toFixed(0).toString();
    this.m_activeInputType = ActiveInputType.None;
    this.m_dimension.Line.Input.Dom.style.border = "1px solid #101010";
    this.m_dimension.Arc.Input.Dom.style.border = "1px solid #101010";
    this.m_inputValue = "";
    this.updateRectangle();
    this.updateXAxisDistanceAuxPoint();
    this.updateDistanceAuxPoint();
  }
  OnDownPoint(params: DrawOperationMovePointParams): void {
    // if (this.points.length <= 1) {
    //   this.m_auxPoints = [];
    // }
    this.points.push(params.point);

    if (this.points.length == 1) {
      this.createdMoveLineAux();
    }
  }
  GetPoints(): Vector3[] {
    return this.points;
  }
  OnWheel(e?: WheelEvent): void {
    if (this.points.length) {
      this.m_dimension.Line.SetDimension(
        this.originPoint,
        this.curPoint,
        this.scale
      );
      let xAxis = new THREE.Vector3(1, 0, 0);

      this.m_dimension.Arc.SetDimension(
        this.originPoint,
        xAxis,
        this.curDir,
        this.scale
      );
      this.updateXAxisDistanceAuxPoint();
      this.updateDistanceAuxPoint();
    }
  }

  get auxPoint() {
    let ps: THREE.Vector3[] = [];
    this.m_auxPoints.forEach((x) => {
      ps.push(x.orgin);
    });
    return ps;
  }

  Finish(operate: DrawOperateState) {
    let quat = new THREE.Quaternion();
    let positions: THREE.Vector3[] = [];
    let columnNum = this.XAxisAuxPointGroup.children.length;
    if (this.XAxisAuxPointGroup.children.length) {
      for (let i = 0; i < this.XAxisAuxPointGroup.children.length; i++) {
        let auxPoint = this.XAxisAuxPointGroup.children[i];
        let position = auxPoint.position.clone();
        positions.push(position);
      }
    }
    if (this.AuxPointGroup.children.length) {
      for (let i = 0; i < this.AuxPointGroup.children.length; i++) {
        let auxPoint = this.AuxPointGroup.children[i];
        let position = auxPoint.position.clone();
        positions.push(position);
      }
    }
    positions.sort((pre, cur) => {
      return cur.y - pre.y;
    });
    this.clear();

    if (this.m_quadraturePoint) {
      this.points.length = 0;
    }
    if (operate == DrawOperateState.complete) {
      if (this.points.length > 1) {
        let key = `${this.m_layer.Id}_${this.m_color}`;
        let lines: DbLine[] = this.m_model.Lines[key]?.lines ?? [];
        let ps: Array<Array<THREE.Vector3>> = [];
        let index = 0;
        let returnLine: DbLine | DBEntity[] = [];
        let scale = new THREE.Vector3(1, 1, 1);
        if (!(this.m_specialParams.scale instanceof Object)) {
          scale.x = this.m_specialParams.scale;
          scale.y = this.m_specialParams.scale;
          scale.z = this.m_specialParams.scale;
        } else {
          scale.x = this.m_specialParams.scale.x;
          scale.y = this.m_specialParams.scale.y;
          scale.z = this.m_specialParams.scale.z;
        }
        for (let i = 0; i < positions.length; i++) {
          let matrix = new THREE.Matrix4();
          let position = positions[i].clone();
          matrix.compose(position, quat, scale);
          this.SignMatrixs.push(matrix);
          if (this.m_specialParams.takeOverType == 1) {
            if (i % this.xAxisAuxPointLength === 0) {
              ps[index] = [];
              for (let j = 0; j < this.xAxisAuxPointLength; j++) {
                ps[index].push(positions[i + j]);
              }
              index++;
            }
          } else if (this.m_specialParams.takeOverType == 2) {
            if (i < this.xAxisAuxPointLength) {
              ps[i] = [];
              for (let j = 0; j < this.yNum; j++) {
                ps[i].push(positions[i + j * this.xAxisAuxPointLength]);
              }
            }
          }
        }
        if (!this.m_specialParams.takeOverType) {
          ps.length = 0;
        }
        for (let i = 0; i < ps.length; i++) {
          let list = ps[i];
          for (let j = 0; j < list.length; j++) {
            if (j == list.length - 1) {
              break;
            }
            let line = new DbLine({
              id: generateUUID(),
              layerId: this.m_layer.Id,
              blockId: "",
              points: new Float32Array([
                list[j].x,
                list[j].y,
                list[j].z,
                list[j + 1].x,
                list[j + 1].y,
                list[j + 1].z,
              ]),
              indexes: new Int32Array([0, 1]),
              color: this.m_color,
              extend_id: generateUUID(),
            });
            let sphere = new THREE.Sphere();
            let box = new THREE.Box3();
            box.setFromArray(line.points);
            box.getBoundingSphere(sphere);
            line.block_path = generateUUID();
            line.sphere = sphere;
            line.box = box;
            lines.push(line);
            returnLine!.push(line);
          }
        }

        if (this.m_model.Lines[key]) {
          this.m_model.Lines[key].lines = lines;
          this.m_context.ToolsManager.LinesToModel(
            lines,
            this.m_model.Lines[key].model
          );
        } else {
          this.m_model.Lines[key] = {
            canEdit: true,
            lines: lines,
            model: this.m_model.addLinesToModel(
              this.m_layer.Id,
              this.m_color,
              lines
            ),
          };
        }

        return {
          dblines: returnLine,
          matrixs: this.SignMatrixs,
          columnNum,
        };
      }
      return [];
    } else if (operate == DrawOperateState.cancel) {
      return [];
    }
    return undefined;
  }

  private get scale() {
    return this.view.ScreenModelScale;
  }
  // 辅助线
  private createdMoveLineAux() {
    this.drawLineAux = this.m_model.m_context.ToolsManager.CreatedAuxLine();
  }
  // 保护半径
  private createdProtectRadius() {
    if (this.m_specialParams.protectRadius.isCreate === false) {
      return;
    }
    let protectRadiusProject = new ProtectRadius(
      this.m_context,
      this.m_specialParams.protectRadius.radius
    ).ProtectRadiusProject;
    return protectRadiusProject;
  }
  private updateXAxisProtectRadius() {
    if (this.m_specialParams.protectRadius.isCreate === false) {
      return;
    }
    this.XAxisAuxPointGroup.children.forEach((auxPoint, index) => {
      let protectRadius = this.XAxisProtectRadiusGroup.children[index];
      if (protectRadius) {
        let position = auxPoint.position.clone();
        protectRadius.position.copy(position);
      }
    });
  }

  private updateProtectRadius() {
    if (this.m_specialParams.protectRadius.isCreate === false) {
      return;
    }

    this.yAxisAuxPointList.forEach((item, i) => {
      item.forEach((auxPoint, j) => {
        let protectRadiusList = this.ProtectRadiusGroup.children[i];
        protectRadiusList.children[j]?.position?.copy(auxPoint.position);
      });
    });
  }

  // X轴辅助标志
  private createdXAxisAuxPoint(position: THREE.Vector3 = new THREE.Vector3()) {
    let geo = new THREE.BufferGeometry();
    let positions = new Float32Array(37 * 3);
    for (let index = 0; index <= 36; index++) {
      let angle = (Math.PI * 2 * index) / 36;
      let x = Math.cos(angle) * 0.2;
      let y = Math.sin(angle) * 0.2;
      let z = 0;
      positions[index * 3] = x;
      positions[index * 3 + 1] = y;
      positions[index * 3 + 2] = z;
    }
    geo.setAttribute("position", new THREE.BufferAttribute(positions, 3));
    let material = new THREE.LineBasicMaterial({ color: CommonColor.White });
    let AuxPoint = new THREE.Line(geo, material);
    AuxPoint.position.copy(position);
    this.XAxisAuxPointGroup.add(AuxPoint);
  }

  // 辅助矩形
  private m_auxRectangle!: THREE.LineSegments;
  private createRectangle() {
    let geo = new THREE.BufferGeometry();
    let material = new THREE.LineBasicMaterial({ color: CommonColor.White });
    this.m_auxRectangle = new THREE.LineSegments(geo, material);
    this.m_auxLayer.add(this.m_auxRectangle);
  }
  private updateRectangle() {
    if (this.points.length) {
      let leftTop = this.points[0];
      let rightBottom = this.curPoint;
      if (this.m_quadraturePoint) {
        return;
      }
      let leftBottom = new THREE.Vector3(leftTop.x, rightBottom.y, 0);
      let rightTop = new THREE.Vector3(rightBottom.x, leftTop.y, 0);
      let positions = new Float32Array([
        leftTop.x,
        leftTop.y,
        leftTop.z,
        rightTop.x,
        rightTop.y,
        rightTop.z,
        rightTop.x,
        rightTop.y,
        rightTop.z,
        rightBottom.x,
        rightBottom.y,
        rightBottom.z,
        rightBottom.x,
        rightBottom.y,
        rightBottom.z,
        leftBottom.x,
        leftBottom.y,
        leftBottom.z,
        leftBottom.x,
        leftBottom.y,
        leftBottom.z,
        leftTop.x,
        leftTop.y,
        leftTop.z,
      ]);
      let { geometry } = this.m_auxRectangle;
      geometry.setAttribute(
        "position",
        new THREE.BufferAttribute(positions, 3)
      );
      this.m_auxRectangle.frustumCulled = false;
    }
  }

  // 创建X轴距离标签
  private XAxisLabelArray: ULabel[] = [];
  // 创建Y轴距离标签
  private YAxisLabelArray: ULabel[] = [];

  private createDistanceLabel() {
    return new ULabel(this.view, this.m_context.Dom);
  }

  private xAxisAuxPointLength = 0;
  private xNum = 0;
  private isXAxisDelete = false;
  private updateXAxisDistanceAuxPoint(point?: THREE.Vector3) {
    let curPoint = this.curPoint;
    if (this.m_quadraturePoint) {
      return;
    }
    let distance = Math.abs(curPoint.x - this.points[0].x) * 1000;
    let xAxisEndPoint = new THREE.Vector3(curPoint.x, this.points[0].y, 0);
    let { offWallDistance, maxInterval, minInterval } = this.m_specialParams;
    let radius1 = 0;
    let ave = distance / 2;
    let remainder = ave % 50;
    let le1 = ave - remainder;
    let le2 = distance - le1;
    let lx = 0;
    let dn = 0;
    this.xNum = 0;
    if (distance > offWallDistance) {
      dn = (distance - offWallDistance) / maxInterval;
      let intPart1 = Math.trunc(dn);
      let fractionalPart = dn - intPart1;
      if (intPart1 > 0) {
        dn = intPart1 + 1;
      } else {
        dn = intPart1;
        if (fractionalPart >= 0.5) {
          dn = intPart1 + 1;
        }
      }
    }
    if (dn >= 1) {
      let dis1 = (distance - offWallDistance) / dn;
      let rem1 = dis1 % 50;
      lx = dis1 - rem1;
      let dis2 = (distance - lx * dn) / 2;
      let rem2 = dis2 % 50;
      le1 = dis2 - rem2;
      le2 = distance - le1 - lx * dn;
    }
    radius1 = le1 / distance;
    this.xNum = dn + 1;
    if (this.xNum != this.xAxisAuxPointLength) {
      if (this.xNum > this.xAxisAuxPointLength) {
        this.isXAxisDelete = false;
        for (let i = 0; i < this.xNum - this.xAxisAuxPointLength; i++) {
          this.createdXAxisAuxPoint();
          let xAxisProtectRadius = this.createdProtectRadius();
          if (xAxisProtectRadius) {
            this.XAxisProtectRadiusGroup.add(xAxisProtectRadius);
          }
        }
        for (let i = 0; i < dn + 2 - this.XAxisLabelArray.length; i++) {
          let label = this.createDistanceLabel();
          this.XAxisLabelArray.push(label);
        }
      } else {
        this.isXAxisDelete = true;
        while (this.XAxisAuxPointGroup.children.length > this.xNum) {
          let lastLength = this.XAxisAuxPointGroup.children.length - 1;
          let delItem = this.XAxisAuxPointGroup.children[lastLength] as any;
          delItem.removeFromParent();
          delItem.geometry.dispose();
          delItem.material.dispose();
          if (this.XAxisProtectRadiusGroup.children.length) {
            let delItem = this.XAxisProtectRadiusGroup.children[
              lastLength
            ] as any;
            delItem.removeFromParent();
            delItem.geometry.dispose();
            delItem.material.dispose();
          }
        }

        for (let i = dn + 1; i < this.XAxisLabelArray.length; i++) {
          let delItem = this.XAxisLabelArray[i];
          delItem.dispose();
          this.XAxisLabelArray.splice(i, 1);
        }
      }
    }
    this.xAxisAuxPointLength = this.xNum;
    let firstAuxPoint = this.XAxisAuxPointGroup.children[0];
    if (this.xAxisAuxPointLength > 1) {
      for (let i = 1; i < this.xAxisAuxPointLength; i++) {
        let auxPoint = this.XAxisAuxPointGroup.children[i];
        let dis = i * lx + le1;
        let radius = dis / distance;
        let position = this.points[0]
          .clone()
          .lerp(xAxisEndPoint.clone(), radius);
        auxPoint.position.copy(position);
      }
    }
    for (let i = 0; i < this.XAxisLabelArray.length; i++) {
      let label = this.XAxisLabelArray[i];
      if (i == 0) {
        let radius = le1 / distance / 2;
        let pos = this.points[0].clone().lerp(xAxisEndPoint.clone(), radius);
        label.SetVaule(le1.toString(), pos, new THREE.Vector3(1, 0, 0));
      } else if (i == this.XAxisLabelArray.length - 1) {
        let radius = le2 / distance / 2;
        let pos = xAxisEndPoint.clone().lerp(this.points[0].clone(), radius);
        label.SetVaule(
          le2.toFixed(0).toString(),
          pos,
          new THREE.Vector3(1, 0, 0)
        );
      } else {
        let dis = (i - 1) * lx + le1;
        let half = lx / 2;
        let radius = (dis + half) / distance;
        let pos = this.points[0].clone().lerp(xAxisEndPoint.clone(), radius);
        label.SetVaule(
          lx.toFixed(0).toString(),
          pos,
          new THREE.Vector3(1, 0, 0)
        );
      }
    }

    // console.log("dn=", dn, "le1=", le1, "le2=", le2, "lx=", lx);
    let position1 = this.points[0].clone().lerp(xAxisEndPoint.clone(), radius1);
    firstAuxPoint.position.copy(position1);
  }

  private yAxisAuxPointLength = 0;
  private yAxisAuxPointList!: [[THREE.Object3D]];
  private yNum = 0;
  private updateDistanceAuxPoint() {
    let curPoint = this.curPoint;
    if (this.m_quadraturePoint) {
      return;
    }
    let isDeleteProtectRadius = false;
    let distance = Math.abs(curPoint.y - this.points[0].y) * 1000;
    let yAxisEndPoint = new THREE.Vector3(this.points[0].x, curPoint.y, 0);
    if (this.yAxisAuxPointLength === 0) {
      this.yAxisAuxPointList = [] as any;
    }

    let { offWallDistance, maxInterval, minInterval } = this.m_specialParams;
    let radius1 = 0;
    let ave = distance / 2;
    let remainder = ave % 50;
    let le1 = ave - remainder;
    let le2 = distance - le1;
    let lx = 0;
    let dn = 0;
    this.yNum = 0;
    if (distance > offWallDistance) {
      dn = (distance - offWallDistance) / maxInterval;
      let intPart1 = Math.trunc(dn);
      let fractionalPart = dn - intPart1;
      if (intPart1 > 0) {
        dn = intPart1 + 1;
      } else {
        dn = intPart1;
        if (fractionalPart >= 0.5) {
          dn = intPart1 + 1;
        }
      }
    }
    if (dn >= 1) {
      let dis1 = (distance - offWallDistance) / dn;
      let rem1 = dis1 % 50;
      lx = dis1 - rem1;
      let dis2 = (distance - lx * dn) / 2;
      let rem2 = dis2 % 50;
      le1 = dis2 - rem2;
      le2 = distance - le1 - lx * dn;
    }
    radius1 = le1 / distance;
    this.yNum = dn + 1;
    if (this.yNum != this.yAxisAuxPointLength) {
      if (this.yNum > this.yAxisAuxPointLength) {
        for (let i = 0; i < dn + 2 - this.YAxisLabelArray.length; i++) {
          let label = this.createDistanceLabel();
          this.YAxisLabelArray.push(label);
        }
      } else {
        for (let i = dn + 1; i < this.YAxisLabelArray.length; i++) {
          let delItem = this.YAxisLabelArray[i];
          delItem.dispose();
          this.YAxisLabelArray.splice(i, 1);
        }

        for (let i = this.yNum; i < this.yAxisAuxPointLength; i++) {
          let delItem = this.yAxisAuxPointList[i - 1];
          for (let j = 0; j < delItem.length; j++) {
            let item = delItem[j] as any;
            item.geometry.dispose();
            item.material.dispose();
            item.removeFromParent();
          }
        }
        this.yAxisAuxPointList.splice(this.yNum - 1);
        if (this.m_specialParams.protectRadius.isCreate) {
          while (this.ProtectRadiusGroup.children.length >= this.yNum) {
            let delItemProtectRadius =
              this.ProtectRadiusGroup.children.pop() as THREE.Group;
            this.view.m_context.ToolsManager.GroupClear(delItemProtectRadius);
            this.ProtectRadiusGroup.remove(delItemProtectRadius);
          }

          isDeleteProtectRadius = true;
        }
      }
    }
    if (this.isXAxisDelete) {
      for (let i = 0; i < this.yAxisAuxPointList.length; i++) {
        let delItemLast = this.yAxisAuxPointList[i];
        while (delItemLast.length > this.xNum) {
          let lastLength = delItemLast.length - 1;
          let delItem = delItemLast[lastLength] as any;
          delItem.removeFromParent();
          delItem.geometry.dispose();
          delItem.material.dispose();
          delItemLast.splice(lastLength, 1);
        }
        if (this.m_specialParams.protectRadius.isCreate) {
          let delItemProtectRadius = this.ProtectRadiusGroup.children[i];
          while (delItemProtectRadius.children.length > this.xNum) {
            let lastLength = delItemProtectRadius.children.length - 1;
            let delItem = delItemProtectRadius.children[lastLength] as any;
            delItem.removeFromParent();
            delItem.geometry.dispose();
            delItem.material.dispose();
          }
        }
      }
    }

    if (this.yNum >= 2) {
      for (let i = 1; i < this.yNum; i++) {
        for (let j = 0; j < this.XAxisAuxPointGroup.children.length; j++) {
          let xAxisAuxPoint = this.XAxisAuxPointGroup.children[j];
          let cloneAuxPoint = xAxisAuxPoint.clone();
          if (!this.yAxisAuxPointList[i - 1]) {
            this.yAxisAuxPointList[i - 1] = [] as any;
          }
          if (this.yAxisAuxPointList[i - 1][j]) {
            this.yAxisAuxPointList[i - 1][j].position.copy(
              cloneAuxPoint.position
            );
          } else {
            this.yAxisAuxPointList[i - 1][j] = cloneAuxPoint;
          }
        }
      }
      for (let i = 1; i < this.yNum; i++) {
        let dis = i * lx + le1;
        let radius = dis / distance;
        let position = this.points[0]
          .clone()
          .lerp(yAxisEndPoint.clone(), radius);
        this.yAxisAuxPointList[i - 1].forEach((item) => {
          item.position.y = position.y;
        });
        i;
      }
    }
    this.yAxisAuxPointList.forEach((item, idx) => {
      item.forEach((auxPoint) => {
        if (!this.AuxPointGroup.getObjectById(auxPoint.id)) {
          this.AuxPointGroup.add(auxPoint);
          // console.log(isDeleteProtectRadius,'isDeleteProtectRadius');
          // if (!isDeleteProtectRadius) {
          let ProtectRadius = this.createdProtectRadius();
          if (ProtectRadius) {
            if (!this.ProtectRadiusGroup.children[idx]) {
              this.ProtectRadiusGroup.children[idx] = new THREE.Group();
            }
            this.ProtectRadiusGroup.children[idx].add(ProtectRadius);
          }
          // }
        }
      });
    });
    // console.log(
    //   this.yAxisAuxPointList,
    //   this.ProtectRadiusGroup.children,
    //   "this.yAxisAuxPointList"
    // );

    for (let i = 0; i < this.YAxisLabelArray.length; i++) {
      let label = this.YAxisLabelArray[i];
      if (i == 0) {
        let radius = le1 / distance / 2;
        let pos = this.points[0].clone().lerp(yAxisEndPoint.clone(), radius);
        label.SetVaule(le1.toString(), pos, new THREE.Vector3(1, 0, 0));
      } else if (i == this.YAxisLabelArray.length - 1) {
        let radius = le2 / distance / 2;
        let pos = yAxisEndPoint.clone().lerp(this.points[0].clone(), radius);
        label.SetVaule(
          le2.toFixed(0).toString(),
          pos,
          new THREE.Vector3(1, 0, 0)
        );
      } else {
        let dis = (i - 1) * lx + le1;
        let half = lx / 2;
        let radius = (dis + half) / distance;
        let pos = this.points[0].clone().lerp(yAxisEndPoint.clone(), radius);
        label.SetVaule(
          lx.toFixed(0).toString(),
          pos,
          new THREE.Vector3(1, 0, 0)
        );
      }
    }
    this.yAxisAuxPointLength = this.yNum;

    // console.log("dn=", dn, "le1=", le1, "le2=", le2, "lx=", lx);
    let position1 = this.points[0].clone().lerp(yAxisEndPoint.clone(), radius1);
    this.XAxisAuxPointGroup.children.forEach((item) => {
      item.position.y = position1.y;
    });
    this.updateXAxisProtectRadius();
    this.updateProtectRadius();
  }

  private clear() {
    this.m_dimension?.dispose();
    this.m_context.ToolsManager.ObjectsClear([
      this.m_locationObject,
      this.m_tempObject!,
      this.drawLineAux!,
      this.m_auxRectangle,
    ]);
    this.m_context.ToolsManager.GroupClear(this.XAxisAuxPointGroup);
    this.m_context.ToolsManager.GroupClear(this.AuxPointGroup);
    this.m_context.ToolsManager.GroupClear(this.XAxisProtectRadiusGroup);
    this.m_context.ToolsManager.GroupClear(this.ProtectRadiusGroup);
    if (this.XAxisLabelArray.length) {
      this.XAxisLabelArray.forEach((label) => {
        label.dispose();
      });
    }
    if (this.YAxisLabelArray.length) {
      this.YAxisLabelArray.forEach((label) => {
        label.dispose();
      });
    }
    if (this.yAxisAuxPointList.length) {
      let list = this.yAxisAuxPointList.flat(1);
      for (let i = 0; i < list.length; i++) {
        let yPoint = list[i] as any;
        yPoint.removeFromParent();
        yPoint.geometry.dispose();
        yPoint.material.dispose();
      }
    }
  }
}

export { DrawRectangleLayout };
